import React, { useEffect, useRef, useState } from 'react'
import config from '../Config'
import { t } from '../i18n'
import { track } from '../Utils/analytics'

const FILL_TIMEOUT_MS = 4000

/* In-feed 300x250 display ad (spec 5.12). The slot keeps its height while the
   ad loads, starts loading about one screen before it scrolls into view, and
   removes itself entirely when no ad comes (no fill, ad blocker), so there's
   never an empty frame. Always labelled "Sponzorisano". */
export default function AdSlot({ position }) {
  const ref = useRef(null)
  const [state, setState] = useState(config.ADS.ENABLED ? 'idle' : 'empty') // idle | loading | filled | empty

  // Load when ~1 screen away.
  useEffect(() => {
    if (state !== 'idle' || !ref.current) return undefined
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return
        io.disconnect()
        setState('loading')
      },
      { rootMargin: '100% 0px' }
    )
    io.observe(ref.current)
    return () => io.disconnect()
  }, [state])

  // The <ins> exists only from 'loading' on: AdSense's push() fills the first
  // unprocessed <ins> on the page, which must be this one.
  useEffect(() => {
    if (state !== 'loading' || !ref.current) return undefined
    const ins = ref.current.querySelector('ins')
    if (!ins.getAttribute('data-adsbygoogle-status')) {
      try {
        ;(window.adsbygoogle = window.adsbygoogle || []).push({})
      } catch (e) {
        setState('empty')
        return undefined
      }
    }
    const settle = () => {
      const status = ins?.getAttribute('data-ad-status')
      if (status === 'filled') setState('filled')
      else if (status === 'unfilled') setState('empty')
    }
    const mo = new MutationObserver(settle)
    mo.observe(ins, { attributes: true, attributeFilter: ['data-ad-status'] })
    settle()
    const timer = setTimeout(() => {
      if (ins?.getAttribute('data-ad-status') !== 'filled') setState('empty')
    }, FILL_TIMEOUT_MS)
    return () => {
      mo.disconnect()
      clearTimeout(timer)
    }
  }, [state])

  useEffect(() => {
    if (state === 'filled') track('ad_slot_filled', { position })
    if (state === 'empty' && config.ADS.ENABLED) track('ad_slot_empty', { position })
  }, [state, position])

  // Impression: at least half visible for a second. Click: the window loses
  // focus to the ad's iframe while the pointer is over the slot.
  useEffect(() => {
    if (state !== 'filled' || !ref.current) return undefined
    let timer = null
    let seen = false
    const io = new IntersectionObserver(
      ([entry]) => {
        clearTimeout(timer)
        if (entry.intersectionRatio >= 0.5 && !seen) {
          timer = setTimeout(() => {
            seen = true
            track('ad_impression', { position })
          }, 1000)
        }
      },
      { threshold: [0, 0.5] }
    )
    io.observe(ref.current)
    let hover = false
    const el = ref.current
    const enter = () => (hover = true)
    const leave = () => (hover = false)
    const blur = () => hover && track('ad_click', { position })
    el.addEventListener('mouseenter', enter)
    el.addEventListener('mouseleave', leave)
    window.addEventListener('blur', blur)
    return () => {
      io.disconnect()
      clearTimeout(timer)
      el.removeEventListener('mouseenter', enter)
      el.removeEventListener('mouseleave', leave)
      window.removeEventListener('blur', blur)
    }
  }, [state, position])

  if (state === 'empty') return null
  return (
    <aside className="feed-item ad-slot" ref={ref} aria-label={t('feed.sponsored')}>
      <div className="ad-label">{t('feed.sponsored')}</div>
      {state === 'idle' ? (
        <div className="ad-unit" />
      ) : (
        <ins
          className="adsbygoogle ad-unit"
          data-ad-client={config.ADS.CLIENT}
          data-ad-slot={config.ADS.FEED_SLOT}
        />
      )}
    </aside>
  )
}
